import { useNewsFeed } from '@/hooks/useNewsFeed'
import { useEffect, useRef } from 'react'
import { FeedHeader } from '@/components/feed/FeedHeader'
import { FeedItem } from '@/components/feed/FeedItem'
import { FeedSkeleton } from '@/components/feed/FeedSkeleton'

export function HomePage() {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
    isRefetching,
  } = useNewsFeed()

  const observerRef = useRef<HTMLDivElement>(null)

  // Infinite scroll: load more when sentinel enters viewport
  useEffect(() => {
    if (!observerRef.current) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: '200px' }
    )
    observer.observe(observerRef.current)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Pull-to-refresh on mobile (uses native overscroll behavior)
  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    let startY = 0
    let pulling = false

    const onTouchStart = (e: TouchEvent) => {
      if (el.scrollTop === 0) {
        startY = e.touches[0].clientY
        pulling = true
      }
    }
    const onTouchMove = (e: TouchEvent) => {
      if (!pulling) return
      const distance = e.touches[0].clientY - startY
      if (distance > 80) {
        pulling = false
        refetch()
      }
    }
    const onTouchEnd = () => {
      pulling = false
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: true })
    el.addEventListener('touchend', onTouchEnd, { passive: true })
    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
    }
  }, [refetch])

  const allItems = data?.pages.flat() ?? []

  return (
    <div ref={containerRef} className="min-h-screen overflow-y-auto">
      <FeedHeader onRefresh={refetch} isRefreshing={isRefetching} />

      <div className="px-4 pb-24 space-y-3 pt-3">
        {isLoading ? (
          <>
            <FeedSkeleton />
            <FeedSkeleton />
            <FeedSkeleton />
          </>
        ) : allItems.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p>No news or announcements yet.</p>
            <p className="text-sm mt-2">Pull down to refresh.</p>
          </div>
        ) : (
          <>
            {allItems.map((item) => (
              <FeedItem key={`${item.type}-${item.id}`} item={item} />
            ))}

            {/* Sentinel for infinite scroll */}
            <div ref={observerRef} className="h-8 flex items-center justify-center">
              {isFetchingNextPage && (
                <div className="text-sm text-muted-foreground">Loading more...</div>
              )}
              {!hasNextPage && allItems.length > 0 && (
                <div className="text-xs text-muted-foreground">You're all caught up</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
